import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, TextInput, Select, Label, Badge, Spinner } from 'flowbite-react';
import { observer } from 'mobx-react-lite';
import { HiUserAdd, HiPencil, HiTrash } from 'react-icons/hi';
import { format } from 'date-fns';
import { userStore } from '../stores/userStore';
import type { User } from '../stores/userStore';

const UsersPage: React.FC = observer(() => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user' as 'root' | 'admin' | 'user',
    password: '',
  });

  useEffect(() => {
    userStore.fetchUsers();
  }, []);

  const getRoleBadge = (role: string) => {
    const roleConfig = {
      'root': { color: 'red', label: 'ルート管理者' },
      'admin': { color: 'blue', label: '管理者' },
      'user': { color: 'gray', label: 'ユーザー' },
    };
    const config = roleConfig[role as keyof typeof roleConfig] || roleConfig['user'];
    return <Badge color={config.color as any}>{config.label}</Badge>;
  };

  const handleAddUser = async () => {
    try {
      await userStore.createUser({
        email: formData.email,
        name: formData.name,
        role: formData.role === 'root' ? 'admin' : formData.role as 'admin' | 'user',
        password: formData.password,
      });
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleEditUser = async () => {
    if (!selectedUser) return;
    try {
      await userStore.updateUser(selectedUser.id, {
        name: formData.name,
        role: formData.role === 'root' ? 'admin' : formData.role as 'admin' | 'user',
        password: formData.password || undefined,
      });
      setShowEditModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to update user:', error);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('このユーザーを削除しますか？')) {
      try {
        await userStore.deleteUser(userId);
      } catch (error) {
        console.error('Failed to delete user:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'user',
      password: '',
    });
    setSelectedUser(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">ユーザー管理</h1>
          <p className="text-gray-600">ユーザーの管理とステータスの確認</p>
        </div>
        <Button onClick={() => setShowAddModal(true)}>
          <HiUserAdd className="mr-2 h-4 w-4" />
          ユーザー追加
        </Button>
      </div>

      {/* ユーザーテーブル */}
      <Card>
        <div className="overflow-x-auto">
          {userStore.loading ? (
            <div className="flex justify-center p-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <Table hoverable>
              <Table.Head>
                <Table.HeadCell>名前</Table.HeadCell>
                <Table.HeadCell>メールアドレス</Table.HeadCell>
                <Table.HeadCell>権限</Table.HeadCell>
                <Table.HeadCell>作成日</Table.HeadCell>
                <Table.HeadCell>最終ログイン</Table.HeadCell>
                <Table.HeadCell>アクション</Table.HeadCell>
              </Table.Head>
              <Table.Body className="divide-y">
                {userStore.users.map((user) => (
                <Table.Row key={user.id} className="bg-white">
                  <Table.Cell className="font-medium">{user.name}</Table.Cell>
                  <Table.Cell>{user.email}</Table.Cell>
                  <Table.Cell>{getRoleBadge(user.role)}</Table.Cell>
                  <Table.Cell>
                    {(user.createdAt || user.created_at)
                      ? format(new Date(user.createdAt || user.created_at || ''), 'yyyy/MM/dd')
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    {(user.lastLoginAt || user.last_login_at)
                      ? format(new Date(user.lastLoginAt || user.last_login_at || ''), 'yyyy/MM/dd HH:mm')
                      : '-'}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="xs"
                        onClick={() => {
                          setSelectedUser(user);
                          setFormData({
                            name: user.name,
                            email: user.email,
                            role: user.role as 'root' | 'admin' | 'user',
                            password: '',
                          });
                          setShowEditModal(true);
                        }}
                      >
                        <HiPencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="xs"
                        color="failure"
                        onClick={() => handleDeleteUser(user.id)}
                      >
                        <HiTrash className="h-3 w-3" />
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
                ))}
              </Table.Body>
            </Table>
          )}
        </div>
      </Card>

      {/* ユーザー追加モーダル */}
      <Modal show={showAddModal} onClose={() => setShowAddModal(false)}>
        <Modal.Header>新規ユーザー追加</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name" value="名前" />
              <TextInput
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="email" value="メールアドレス" />
              <TextInput
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="password" value="パスワード" />
              <TextInput
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                helperText="8文字以上、大文字・小文字・数字・記号を含む必要があります"
              />
            </div>
            <div>
              <Label htmlFor="role" value="権限" />
              <Select
                id="role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'admin' | 'user' })}
              >
                <option value="user">ユーザー</option>
                <option value="admin">管理者</option>
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleAddUser}>追加</Button>
          <Button color="gray" onClick={() => setShowAddModal(false)}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ユーザー編集モーダル */}
      <Modal show={showEditModal} onClose={() => setShowEditModal(false)}>
        <Modal.Header>ユーザー編集</Modal.Header>
        <Modal.Body>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name" value="名前" />
              <TextInput
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-email" value="メールアドレス（変更不可）" />
              <TextInput
                id="edit-email"
                type="email"
                value={formData.email}
                disabled
                helperText="メールアドレスは変更できません"
              />
            </div>
            <div>
              <Label htmlFor="edit-password" value="新しいパスワード（変更する場合のみ）" />
              <TextInput
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="変更しない場合は空欄"
                helperText="8文字以上、大文字・小文字・数字・記号を含む必要があります"
              />
            </div>
            <div>
              <Label htmlFor="edit-role" value="権限" />
              <Select
                id="edit-role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as 'root' | 'admin' | 'user' })}
              >
                <option value="user">ユーザー</option>
                <option value="admin">管理者</option>
              </Select>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleEditUser}>更新</Button>
          <Button color="gray" onClick={() => setShowEditModal(false)}>
            キャンセル
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default UsersPage;